import React, { useState } from 'react';
import { getAuth, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

const DeleteModal = ({
    type, // 'single', 'bulk', or 'all'
    isOpen,
    onClose,
    onConfirm,
    onCancel, // New: callback for canceling during deletion
    recordInfo, // For single delete: { address, skipId }, For bulk: { count, progress, total }, For all: { count }
    isProcessing = false,
    isDeletingProgress = false // New: true when showing progress bar
}) => {
    const [step, setStep] = useState(1); // For 'all' type: 1=warning, 2=password, 3=final
    const [password, setPassword] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handlePasswordVerification = async () => {
        setError('');
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user || !user.email) {
            setError('No user is currently logged in');
            return;
        }

        try {
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);
            setPassword('');
            setStep(3);
        } catch (error) {
            console.error('Re-authentication error:', error);
            if (error.code === 'auth/wrong-password') {
                setError('Incorrect password. Please try again.');
            } else if (error.code === 'auth/too-many-requests') {
                setError('Too many failed attempts. Please try again later.');
            } else {
                setError('Authentication failed. Please try again.');
            }
        }
    };

    const handleConfirm = () => {
        if (type === 'all' && confirmText !== 'DELETEALL') {
            setError('Please type DELETEALL exactly as shown');
            return;
        }
        onConfirm();
        handleClose();
    };

    const handleClose = () => {
        setStep(1);
        setPassword('');
        setConfirmText('');
        setError('');
        onClose();
    };

    // Single delete modal
    if (type === 'single') {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '24px',
                    maxWidth: '500px',
                    width: '90%'
                }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#EF4444' }}>
                        Delete Record?
                    </h2>
                    <p style={{ marginBottom: '16px', color: '#374151' }}>
                        Are you sure you want to delete this record?
                    </p>
                    <div style={{
                        backgroundColor: '#F3F4F6',
                        padding: '12px',
                        borderRadius: '4px',
                        marginBottom: '16px'
                    }}>
                        <p style={{ fontSize: '14px', marginBottom: '8px' }}>
                            <strong>Address:</strong> {recordInfo?.address || 'N/A'}
                        </p>
                        <p style={{ fontSize: '14px' }}>
                            <strong>Skip ID:</strong> {recordInfo?.skipId || 'N/A'}
                        </p>
                    </div>
                    <p style={{ fontSize: '14px', color: '#EF4444', marginBottom: '24px' }}>
                        This action cannot be undone.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={handleClose}
                            disabled={isProcessing}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: 'white',
                                border: '1px solid #D1D5DB',
                                borderRadius: '4px',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={isProcessing}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#EF4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            {isProcessing ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Bulk delete modal
    if (type === 'bulk') {
        // Show progress bar if deletion is in progress
        if (isDeletingProgress) {
            const progress = recordInfo?.progress || 0;
            const total = recordInfo?.total || recordInfo?.count || 0;
            const percentage = total > 0 ? Math.round((progress / total) * 100) : 0;

            return (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '32px',
                        maxWidth: '500px',
                        width: '90%'
                    }}>
                        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#374151', textAlign: 'center' }}>
                            Deleting Records...
                        </h2>

                        <p style={{ marginBottom: '20px', color: '#6B7280', textAlign: 'center', fontSize: '16px' }}>
                            {progress} of {total} deleted
                        </p>

                        {/* Progress bar */}
                        <div style={{
                            width: '100%',
                            height: '24px',
                            backgroundColor: '#E5E7EB',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                width: `${percentage}%`,
                                height: '100%',
                                backgroundColor: '#3B82F6',
                                transition: 'width 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}>
                                {percentage}%
                            </div>
                        </div>

                        <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px', textAlign: 'center' }}>
                            Please wait while records are being deleted. You can cancel this operation, but already deleted records cannot be recovered.
                        </p>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={onCancel}
                                style={{
                                    padding: '10px 24px',
                                    backgroundColor: '#EF4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#EF4444'}
                            >
                                Cancel Deletion
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // Show confirmation dialog
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '24px',
                    maxWidth: '500px',
                    width: '90%'
                }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#EF4444' }}>
                        Last chance.
                    </h2>
                    <p style={{ marginBottom: '16px', color: '#374151' }}>
                        You are about to delete {recordInfo?.count} skip record{recordInfo?.count !== 1 ? 's' : ''}.
                    </p>
                    <p style={{ fontSize: '14px', color: '#EF4444', marginBottom: '24px' }}>
                        This action cannot be undone.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={handleClose}
                            disabled={isProcessing}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: 'white',
                                border: '1px solid #D1D5DB',
                                borderRadius: '4px',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={isProcessing}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#EF4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            {isProcessing ? 'Starting...' : 'I understand and accept'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Delete all modal (3-step process)
    if (type === 'all') {
        // Step 1: Warning
        if (step === 1) {
            return (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%'
                    }}>
                        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#EF4444' }}>
                            ⚠️ WARNING: Delete All Records
                        </h2>
                        <p style={{ marginBottom: '16px', color: '#374151' }}>
                            You are about to permanently delete ALL skip records for your company.
                        </p>
                        <div style={{
                            backgroundColor: '#FEF2F2',
                            border: '1px solid #FCA5A5',
                            padding: '12px',
                            borderRadius: '4px',
                            marginBottom: '16px'
                        }}>
                            <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                                Records to be deleted: {recordInfo?.count} skip{recordInfo?.count !== 1 ? 's' : ''}
                            </p>
                            <p style={{ fontSize: '14px', marginBottom: '4px' }}>This will delete:</p>
                            <ul style={{ marginLeft: '20px', fontSize: '14px' }}>
                                <li>All skip records from database</li>
                                <li>All associated images from storage</li>
                            </ul>
                        </div>
                        <p style={{ fontSize: '14px', color: '#EF4444', fontWeight: '600', marginBottom: '24px' }}>
                            This action CANNOT be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={handleClose}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: 'white',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => setStep(2)}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#F59E0B',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // Step 2: Password verification
        if (step === 2) {
            return (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%'
                    }}>
                        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                            Confirm Your Identity
                        </h2>
                        <p style={{ marginBottom: '16px', color: '#6B7280' }}>
                            Please enter your password to continue:
                        </p>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handlePasswordVerification()}
                            placeholder="Your password"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #D1D5DB',
                                borderRadius: '4px',
                                marginBottom: '8px',
                                fontSize: '14px'
                            }}
                        />
                        {error && (
                            <p style={{ color: '#EF4444', fontSize: '14px', marginBottom: '16px' }}>
                                {error}
                            </p>
                        )}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button
                                onClick={handleClose}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: 'white',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePasswordVerification}
                                disabled={!password}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: password ? '#3B82F6' : '#D1D5DB',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: password ? 'pointer' : 'not-allowed',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Verify
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // Step 3: Final confirmation
        if (step === 3) {
            return (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%'
                    }}>
                        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#EF4444' }}>
                            FINAL CONFIRMATION
                        </h2>
                        <p style={{ marginBottom: '16px', color: '#374151' }}>
                            Type <strong>DELETEALL</strong> to confirm deletion:
                        </p>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => {
                                setConfirmText(e.target.value);
                                setError('');
                            }}
                            onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
                            placeholder="Type DELETEALL"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #D1D5DB',
                                borderRadius: '4px',
                                marginBottom: '8px',
                                fontSize: '14px'
                            }}
                        />
                        {error && (
                            <p style={{ color: '#EF4444', fontSize: '14px', marginBottom: '16px' }}>
                                {error}
                            </p>
                        )}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button
                                onClick={handleClose}
                                disabled={isProcessing}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: 'white',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={confirmText !== 'DELETEALL' || isProcessing}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: confirmText === 'DELETEALL' && !isProcessing ? '#EF4444' : '#D1D5DB',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: confirmText === 'DELETEALL' && !isProcessing ? 'pointer' : 'not-allowed',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                {isProcessing ? 'Deleting...' : 'Delete All Records'}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
    }

    return null;
};

export default DeleteModal;
